import type { Component } from "@/types";
import type {
  ChartDataSource,
  CompositeQueryDefinition,
} from "@/types/chartDataSource";
import { queryTemplateApi } from "@/services/api/queryTemplateApi";
import { schemaApi } from "@/services/api/schemaApi";
import { groupApi } from "@/services/api/groupApi";
import { useOrganizationStore, type Organization } from "@/stores/organization/organization-store";
import type { PageWithComponentsForPersistence } from "./persistedProjectState";
import {
  annotateCompositeQueryDefinitionOrganizations,
  getCompositeDefinitionOrganizationScope,
  type CompositeSourceScopeMetadata,
} from "@/components/ai/compositeScope";

interface OrganizationScope {
  organizationId: string;
  organizationName?: string;
}

type ScopeResolution =
  | { kind: "none" }
  | { kind: "single"; scope: OrganizationScope }
  | { kind: "ambiguous" };

interface RepairContext {
  visibleOrganizations: Organization[];
  templateScopeCache: Map<string, Promise<ScopeResolution>>;
  schemaScopeCache: Map<string, Promise<ScopeResolution>>;
  groupScopeCache: Map<string, Promise<ScopeResolution>>;
}

const SCOPE_LOOKUP_CONCURRENCY_LIMIT = 4;
const COMPONENT_REPAIR_CONCURRENCY_LIMIT = 4;

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

async function mapWithConcurrencyLimit<TInput, TOutput>(
  items: readonly TInput[],
  limit: number,
  mapper: (item: TInput, index: number) => Promise<TOutput>,
): Promise<TOutput[]> {
  if (items.length === 0) {
    return [];
  }

  const results: TOutput[] = new Array(items.length);
  let nextIndex = 0;
  const workerCount = Math.min(limit, items.length);

  await Promise.all(
    Array.from({ length: workerCount }, async () => {
      while (nextIndex < items.length) {
        const currentIndex = nextIndex;
        nextIndex += 1;
        results[currentIndex] = await mapper(items[currentIndex], currentIndex);
      }
    }),
  );

  return results;
}

function normalizeOptionalString(value: string | undefined): string | undefined {
  if (typeof value !== "string") {
    return undefined;
  }

  const trimmedValue = value.trim();
  return trimmedValue ? trimmedValue : undefined;
}

function createScope(
  organizationId?: string,
  organizationName?: string,
): OrganizationScope | null {
  const normalizedOrganizationId = normalizeOptionalString(organizationId);
  if (!normalizedOrganizationId) {
    return null;
  }

  const normalizedOrganizationName = normalizeOptionalString(organizationName);
  return normalizedOrganizationName
    ? {
        organizationId: normalizedOrganizationId,
        organizationName: normalizedOrganizationName,
      }
    : {
        organizationId: normalizedOrganizationId,
      };
}

function collectSingleScope(scopes: OrganizationScope[]): ScopeResolution {
  if (scopes.length === 0) {
    return { kind: "none" };
  }

  const scopesById = new Map<string, OrganizationScope[]>();
  scopes.forEach((scope) => {
    const existingScopes = scopesById.get(scope.organizationId) ?? [];
    existingScopes.push(scope);
    scopesById.set(scope.organizationId, existingScopes);
  });

  if (scopesById.size !== 1) {
    return { kind: "ambiguous" };
  }

  const [organizationId, groupedScopes] = Array.from(scopesById.entries())[0];
  if (!organizationId || !groupedScopes) {
    return { kind: "none" };
  }

  const organizationNames = groupedScopes
    .map((scope) => normalizeOptionalString(scope.organizationName))
    .filter((name): name is string => Boolean(name));
  const firstOrganizationName = organizationNames[0];
  const organizationName =
    firstOrganizationName &&
    organizationNames.every((name) => name === firstOrganizationName)
      ? firstOrganizationName
      : undefined;

  return organizationName
    ? { kind: "single", scope: { organizationId, organizationName } }
    : { kind: "single", scope: { organizationId } };
}

function getVisibleOrganizations(): Organization[] {
  const organizationState = useOrganizationStore.getState();
  const organizationsById = new Map<string, Organization>();

  [...organizationState.organizations, ...organizationState.allOrganizations].forEach((organization) => {
    organizationsById.set(organization._id, organization);
  });

  return Array.from(organizationsById.values());
}

function withVisibleOrganizationName(
  scope: OrganizationScope,
  visibleOrganizations: Organization[],
): OrganizationScope {
  if (normalizeOptionalString(scope.organizationName)) {
    return scope;
  }

  const matchingOrganization = visibleOrganizations.find(
    (organization) => organization._id === scope.organizationId,
  );

  return matchingOrganization
    ? {
        organizationId: scope.organizationId,
        organizationName: matchingOrganization.name,
      }
    : scope;
}

function getComponentQueryTemplateId(component: Component): string | null {
  const queryTemplateId = component.properties["queryTemplateId"];
  if (typeof queryTemplateId !== "string") {
    return null;
  }

  const normalizedTemplateId = queryTemplateId.trim();
  return normalizedTemplateId || null;
}

function isChartDataSource(value: unknown): value is ChartDataSource {
  return isRecord(value) && typeof value.type === "string";
}

function getComponentDataSource(component: Component): ChartDataSource | null {
  const dataSource = component.properties["dataSource"];
  return isChartDataSource(dataSource) ? dataSource : null;
}

function getScopedAxiosConfig(organizationId: string): { headers: { "X-Organization-Id": string } } {
  return {
    headers: {
      "X-Organization-Id": organizationId,
    },
  };
}

async function getTemplateScope(
  templateId: string,
  context: RepairContext,
): Promise<ScopeResolution> {
  const cachedScope = context.templateScopeCache.get(templateId);
  if (cachedScope) {
    return cachedScope;
  }

  const templateScopePromise = (async (): Promise<ScopeResolution> => {
    const visibleOrganizations = context.visibleOrganizations;
    const scopedTemplateLookups = await mapWithConcurrencyLimit(
      visibleOrganizations,
      SCOPE_LOOKUP_CONCURRENCY_LIMIT,
      async (organization) => {
        try {
          return await queryTemplateApi.getById(templateId, {
            organizationId: organization._id,
          });
        } catch {
          return null;
        }
      },
    );

    const templateScopes = scopedTemplateLookups
      .map((template) => createScope(template?.organizationId))
      .filter((scope): scope is OrganizationScope => scope !== null);

    return collectSingleScope(templateScopes);
  })();

  context.templateScopeCache.set(templateId, templateScopePromise);
  return templateScopePromise;
}

async function getSchemaScope(
  schemaId: string,
  context: RepairContext,
): Promise<ScopeResolution> {
  const cachedScope = context.schemaScopeCache.get(schemaId);
  if (cachedScope) {
    return cachedScope;
  }

  const schemaScopePromise = (async (): Promise<ScopeResolution> => {
    const visibleOrganizations = context.visibleOrganizations;
    const scopedSchemaLookups = await mapWithConcurrencyLimit(
      visibleOrganizations,
      SCOPE_LOOKUP_CONCURRENCY_LIMIT,
      async (organization) => {
        try {
          return await schemaApi.getSchema(
            schemaId,
            getScopedAxiosConfig(organization._id),
          );
        } catch {
          return null;
        }
      },
    );

    const schemaScopes = scopedSchemaLookups
      .map((schema) => createScope(schema?.organizationId))
      .filter((scope): scope is OrganizationScope => scope !== null);

    return collectSingleScope(schemaScopes);
  })();

  context.schemaScopeCache.set(schemaId, schemaScopePromise);
  return schemaScopePromise;
}

async function getGroupScope(
  groupId: string,
  context: RepairContext,
): Promise<ScopeResolution> {
  const cachedScope = context.groupScopeCache.get(groupId);
  if (cachedScope) {
    return cachedScope;
  }

  const groupScopePromise = (async (): Promise<ScopeResolution> => {
    const groupScopes: OrganizationScope[] = [];

    try {
      const group = await groupApi.getGroup(groupId);
      const groupScope = createScope(group.organizationId);
      if (groupScope) {
        groupScopes.push(groupScope);
      }

      const assignedScopes = (group.assignedOrganizations ?? [])
        .map((assignment) => createScope(assignment.organizationId))
        .filter((scope): scope is OrganizationScope => scope !== null);
      groupScopes.push(...assignedScopes);
    } catch {
      // Ignore and fall back to assigned-organization lookup only.
    }

    try {
      const assignedOrganizations = await groupApi.getAssignedOrganizations(groupId);
      const assignedScopes = assignedOrganizations
        .map((assignment) => createScope(assignment.organizationId))
        .filter((scope): scope is OrganizationScope => scope !== null);
      groupScopes.push(...assignedScopes);
    } catch {
      // Leave fail-closed when group metadata cannot be resolved.
    }

    return collectSingleScope(groupScopes);
  })();

  context.groupScopeCache.set(groupId, groupScopePromise);
  return groupScopePromise;
}

function getExistingQueryScope(dataSource: ChartDataSource): ScopeResolution {
  const scopeCandidates = [
    createScope(dataSource.organizationId, dataSource.organizationName),
    createScope(
      dataSource.queryDefinition?.organizationId,
      dataSource.queryDefinition?.organizationName,
    ),
  ].filter((scope): scope is OrganizationScope => scope !== null);

  return collectSingleScope(scopeCandidates);
}

function applyQueryScope(
  dataSource: ChartDataSource,
  scope: OrganizationScope,
): { dataSource: ChartDataSource; repaired: boolean } {
  const existingDataSourceScope = createScope(
    dataSource.organizationId,
    dataSource.organizationName,
  );
  const existingQueryScope = createScope(
    dataSource.queryDefinition?.organizationId,
    dataSource.queryDefinition?.organizationName,
  );

  const repairedDataSource: ChartDataSource = {
    ...dataSource,
    organizationId: scope.organizationId,
    ...(scope.organizationName
      ? { organizationName: scope.organizationName }
      : {}),
    ...(dataSource.queryDefinition
      ? {
          queryDefinition: {
            ...dataSource.queryDefinition,
            organizationId: scope.organizationId,
            ...(scope.organizationName
              ? { organizationName: scope.organizationName }
              : {}),
          },
        }
      : {}),
  };

  const repaired =
    existingDataSourceScope?.organizationId !== scope.organizationId ||
    existingQueryScope?.organizationId !== scope.organizationId ||
    (!!scope.organizationName &&
      (existingDataSourceScope?.organizationName !== scope.organizationName ||
        existingQueryScope?.organizationName !== scope.organizationName));

  return {
    dataSource: repairedDataSource,
    repaired,
  };
}

async function resolveAuthoritativeQueryScope(
  dataSource: ChartDataSource,
  queryTemplateId: string | null,
  context: RepairContext,
): Promise<ScopeResolution> {
  const authoritativeScopePromises: Array<Promise<ScopeResolution>> = [];

  if (queryTemplateId) {
    authoritativeScopePromises.push(getTemplateScope(queryTemplateId, context));
  }

  const schemaId = normalizeOptionalString(dataSource.queryDefinition?.schemaId);
  if (schemaId) {
    authoritativeScopePromises.push(getSchemaScope(schemaId, context));
  }

  const groupId = normalizeOptionalString(dataSource.queryDefinition?.groupId);
  if (groupId) {
    authoritativeScopePromises.push(getGroupScope(groupId, context));
  }

  const authoritativeScopes = await Promise.all(authoritativeScopePromises);
  if (authoritativeScopes.some((scope) => scope.kind === "ambiguous")) {
    return { kind: "ambiguous" };
  }

  const singleScopes = authoritativeScopes
    .filter((scope): scope is Extract<ScopeResolution, { kind: "single" }> => scope.kind === "single")
    .map((scope) => scope.scope);

  return collectSingleScope(singleScopes);
}

async function repairQueryDataSource(
  dataSource: ChartDataSource,
  queryTemplateId: string | null,
  context: RepairContext,
): Promise<{ dataSource: ChartDataSource; repaired: boolean }> {
  const existingScope = getExistingQueryScope(dataSource);
  if (existingScope.kind === "ambiguous") {
    return { dataSource, repaired: false };
  }

  const authoritativeScope = await resolveAuthoritativeQueryScope(
    dataSource,
    queryTemplateId,
    context,
  );
  if (authoritativeScope.kind !== "single") {
    return { dataSource, repaired: false };
  }

  return applyQueryScope(
    dataSource,
    withVisibleOrganizationName(authoritativeScope.scope, context.visibleOrganizations),
  );
}

function getPersistedCompositeResolution(
  dataSource: ChartDataSource,
): ScopeResolution {
  const definition = dataSource.compositeQueryDefinition;
  if (!definition) {
    return createScope(dataSource.organizationId, dataSource.organizationName)
      ? collectSingleScope(
          [
            createScope(dataSource.organizationId, dataSource.organizationName),
          ].filter((scope): scope is OrganizationScope => scope !== null),
        )
      : { kind: "none" };
  }

  const scopedDefinition = getCompositeDefinitionOrganizationScope(definition);
  return scopedDefinition
    ? {
        kind: "single",
        scope: scopedDefinition,
      }
    : createScope(dataSource.organizationId, dataSource.organizationName)
      ? collectSingleScope(
          [
            createScope(dataSource.organizationId, dataSource.organizationName),
          ].filter((scope): scope is OrganizationScope => scope !== null),
        )
      : { kind: "none" };
}

async function resolveCompositeSourceMetadata(
  definition: CompositeQueryDefinition,
  context: RepairContext,
): Promise<{ resolution: ScopeResolution; sourceMetadata: CompositeSourceScopeMetadata[] }> {
  const sourceMetadataResults = await mapWithConcurrencyLimit(
    definition.sources,
    COMPONENT_REPAIR_CONCURRENCY_LIMIT,
    async (source) => {
      const sourceDataSource: ChartDataSource = {
        type: "query-template",
        queryDefinition: source.query,
      };
      const sourceScope = await repairQueryDataSource(sourceDataSource, null, context);
      const normalizedScope = createScope(
        sourceScope.dataSource.queryDefinition?.organizationId,
        sourceScope.dataSource.queryDefinition?.organizationName,
      );

      return {
        schemaId: source.query.schemaId,
        scope: normalizedScope,
      };
    },
  );

  if (sourceMetadataResults.some((result) => result.scope === null)) {
    return { resolution: { kind: "none" }, sourceMetadata: [] };
  }

  const scopedMetadata = sourceMetadataResults
    .filter((result): result is { schemaId: string; scope: OrganizationScope } => result.scope !== null)
    .map((result) => ({
      schemaId: result.schemaId,
      organizationId: result.scope.organizationId,
      organizationName: result.scope.organizationName,
    }));

  return {
    resolution: collectSingleScope(
      scopedMetadata
        .map((metadata) => createScope(metadata.organizationId, metadata.organizationName))
        .filter((scope): scope is OrganizationScope => scope !== null),
    ),
    sourceMetadata: scopedMetadata,
  };
}

async function repairCompositeDataSource(
  dataSource: ChartDataSource,
  queryTemplateId: string | null,
  context: RepairContext,
): Promise<{ dataSource: ChartDataSource; repaired: boolean }> {
  const persistedScope = getPersistedCompositeResolution(dataSource);
  if (persistedScope.kind === "ambiguous") {
    return { dataSource, repaired: false };
  }

  const templateScope = queryTemplateId
    ? await getTemplateScope(queryTemplateId, context)
    : { kind: "none" } satisfies ScopeResolution;
  if (templateScope.kind === "ambiguous") {
    return { dataSource, repaired: false };
  }

  const definition = dataSource.compositeQueryDefinition;
  if (!definition) {
    return { dataSource, repaired: false };
  }

  const {
    resolution: sourceScopeResolution,
    sourceMetadata,
  } = await resolveCompositeSourceMetadata(definition, context);
  if (sourceScopeResolution.kind === "ambiguous") {
    return { dataSource, repaired: false };
  }

  const finalScope = collectSingleScope(
    [
      ...(templateScope.kind === "single" ? [templateScope.scope] : []),
      ...(sourceScopeResolution.kind === "single"
        ? [sourceScopeResolution.scope]
        : []),
    ],
  );
  if (finalScope.kind !== "single") {
    return { dataSource, repaired: false };
  }
  const resolvedScope = withVisibleOrganizationName(
    finalScope.scope,
    context.visibleOrganizations,
  );

  const annotatedDefinition =
    sourceMetadata.length === definition.sources.length
      ? annotateCompositeQueryDefinitionOrganizations(definition, sourceMetadata)
      : definition;

  return {
    dataSource: {
      ...dataSource,
      organizationId: resolvedScope.organizationId,
      ...(resolvedScope.organizationName
        ? { organizationName: resolvedScope.organizationName }
        : {}),
      compositeQueryDefinition: annotatedDefinition,
    },
    repaired: true,
  };
}

async function repairComponentDataSource(
  component: Component,
  context: RepairContext,
): Promise<Component> {
  const dataSource = getComponentDataSource(component);
  if (!dataSource) {
    return component;
  }

  const queryTemplateId = getComponentQueryTemplateId(component);

  const repairedDataSource =
    dataSource.type === "composite-query"
      ? await repairCompositeDataSource(dataSource, queryTemplateId, context)
      : dataSource.type === "query-template" || dataSource.type === "timestamp-aggregate"
        ? await repairQueryDataSource(dataSource, queryTemplateId, context)
        : { dataSource, repaired: false };

  if (!repairedDataSource.repaired) {
    return component;
  }

  return {
    ...component,
    properties: {
      ...component.properties,
      dataSource: repairedDataSource.dataSource,
    },
  };
}

export async function repairPagesWithComponentsBindingScopes(
  pagesWithComponents: PageWithComponentsForPersistence[],
): Promise<PageWithComponentsForPersistence[]> {
  const organizationState = useOrganizationStore.getState();
  if (organizationState.currentContext?.type !== "user") {
    return pagesWithComponents;
  }

  const context: RepairContext = {
    visibleOrganizations: getVisibleOrganizations(),
    templateScopeCache: new Map(),
    schemaScopeCache: new Map(),
    groupScopeCache: new Map(),
  };

  return mapWithConcurrencyLimit(
    pagesWithComponents,
    COMPONENT_REPAIR_CONCURRENCY_LIMIT,
    async (pageWithComponents) => ({
      ...pageWithComponents,
      components: await mapWithConcurrencyLimit(
        pageWithComponents.components,
        COMPONENT_REPAIR_CONCURRENCY_LIMIT,
        (component) => repairComponentDataSource(component, context),
      ),
    }),
  );
}
